import React, { useEffect, useState } from 'react';
import { Grid, Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { UnitEnumResponse } from '.generated/api';
import { convertFromSI } from 'helpers/units';

const logStackProGrey = '#4e6072';

const useStyles = makeStyles((theme) => ({
  bar: {
    backgroundColor: logStackProGrey,
    height: '0.9rem',
    minWidth: '0.4rem',
    filter: 'blur(0)', // fixes box shadow rendering in some pdf viewers.
  },
  leftBarLabel: {
    whiteSpace: 'nowrap',
    paddingRight: theme.spacing(1),
    fontWeight: 'bold',
  },
  rightBarLabel: {
    whiteSpace: 'nowrap',
    paddingLeft: theme.spacing(1),
    fontWeight: 'bold',
  },
  barLabel: {
    height: '0.9rem',
  },
  stretch: {
    flexGrow: 1,
  },
  barCenter: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    textAlign: 'center',
    whiteSpace: 'nowrap',
  },
}));

export type BinLimit = {
  label: React.ReactNode;
  min?: number;
  max?: number;
};

export function labelFromLimit(min?: number, max?: number) {
  if (min && max) {
    return (
      <span>
        <b>{min}</b>
        {' ≤ ⌀ < '}
        <b>{max}</b>
      </span>
    );
  }

  if (min) {
    return (
      <span>
        <b>{min}</b>
        {' ≤ ⌀'}
      </span>
    );
  }

  if (max) {
    return (
      <span>
        {'⌀ < '}
        <b>{max}</b>
      </span>
    );
  }

  return '';
}
export function calcDiameterDistributionData(
  binLimits: BinLimit[],
  diameters: number[],

  length: number,
  netVolume: number,
  netVolumeUnit: UnitEnumResponse,
): { label: React.ReactNode; count: number; volume: number }[] {
  const bins = binLimits.map((binLimit) => ({
    limit: binLimit,
    count: 0,
    volume: 0,
  }));

  let totalVolume = 0;
  for (const diameter of diameters) {
    const bin = bins.find(
      (bin) =>
        (!bin.limit.min || diameter >= bin.limit.min) &&
        (!bin.limit.max || diameter < bin.limit.max),
    );
    if (bin) {
      bin.count++;
      const radius = diameter / 2; // radius converted from cm to m
      const volume = length * Math.PI * radius * radius;
      totalVolume += volume;
      bin.volume += volume;
    } else {
      console.warn('Could not find bin for diameter', diameter);
    }
  }

  return bins.map((bin) => ({
    label: bin.limit.label,
    count: bin.count,
    volume:
      convertFromSI(netVolumeUnit, (netVolume * bin.volume) / totalVolume) ?? 0,
  }));
}

export type DiameterDistributionChartProps = {
  countTitle: string;
  volumeTitle: string;
  diameterUnit: string;
  volumeUnit: string;
  distribution: Array<{
    label: React.ReactNode;
    count: number;
    volume: number;
  }>;
};

function DiameterDistributionChart({
  countTitle,
  volumeTitle,
  volumeUnit,
  diameterUnit,
  distribution,
}: DiameterDistributionChartProps): React.ReactElement {
  const classes = useStyles();
  const [maxCount, setMaxCount] = useState(0);
  const [maxVolume, setMaxVolume] = useState(0);
  useEffect(() => {
    setMaxCount(Math.max(...distribution.map((dist) => dist.count)));
    setMaxVolume(Math.max(...distribution.map((dist) => dist.volume)));
  }, [distribution]);

  const countPercentage = (count: number): string => {
    const percentage = Math.round(70.0 * (count / maxCount));
    return `${percentage}%`;
  };

  const volumePercentage = (count: number): string => {
    const percentage = Math.round(60.0 * (count / maxVolume));
    return `${percentage}%`;
  };

  return (
    <Grid container direction="row" wrap="nowrap">
      <Grid item className={classes.stretch}>
        <Grid container spacing={1} direction="column" alignItems="flex-end">
          <Grid item className={classes.leftBarLabel}>
            {countTitle}
          </Grid>
          {distribution.map((dist, i) => {
            return (
              <Grid
                item
                key={i}
                style={{
                  width: '10rem',
                }}
              >
                <Grid
                  container
                  direction="row"
                  alignItems="center"
                  justify="flex-end"
                  wrap="nowrap"
                >
                  <span className={classes.leftBarLabel}>{dist.count}</span>
                  <Paper
                    elevation={3}
                    className={classes.bar}
                    style={{
                      width: countPercentage(dist.count),
                    }}
                  ></Paper>
                </Grid>
              </Grid>
            );
          })}
        </Grid>
      </Grid>
      <Grid item className={classes.barCenter}>
        <Grid container spacing={1} direction="column" alignContent="center">
          <Grid item>
            <div className={classes.barLabel}>
              <b>{diameterUnit}</b>
            </div>
          </Grid>

          {distribution.map((dist, i) => {
            return (
              <Grid item key={i}>
                <div className={classes.barLabel}>{dist.label}</div>
              </Grid>
            );
          })}
        </Grid>
      </Grid>
      <Grid item className={classes.stretch}>
        <Grid container spacing={1} direction="column" alignItems="flex-start">
          <Grid item className={classes.rightBarLabel}>
            {volumeTitle}
          </Grid>
          {distribution.map((dist, i) => {
            return (
              <Grid
                item
                key={i}
                style={{
                  width: '10rem',
                }}
              >
                <Grid
                  container
                  direction="row"
                  alignItems="center"
                  justify="flex-start"
                  wrap="nowrap"
                >
                  <Paper
                    elevation={3}
                    className={classes.bar}
                    style={{
                      width: volumePercentage(dist.volume),
                      // minWidth: '0.4rem',
                    }}
                  ></Paper>
                  <span className={classes.rightBarLabel}>
                    {isNaN(dist.volume) ? 0 : dist.volume.toFixed(3)}{' '}
                    {volumeUnit}
                  </span>
                </Grid>
              </Grid>
            );
          })}
        </Grid>
      </Grid>
    </Grid>
  );
}

export default DiameterDistributionChart;
